import { useState, useCallback, useRef, useMemo } from 'react';

import { logger } from '@azure/storage-blob';
import {
  DndContext,
  DragOverlay,
  useSensor,
  useSensors,
  MouseSensor,
  TouchSensor,
  PointerSensor,
  DragEndEvent,
  DragStartEvent,
  defaultDropAnimation,
  rectIntersection,
} from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import styled from '@emotion/styled';
import { Box, Theme, Typography } from '@mui/material';

import { IcFire, IcMedicalCross, IcPolice } from '@/assets/images';
import { AgencyType, IUnitShift } from '@/models';
import { colors } from '@/theme/variables';

const UnitDragOverlayContainer = styled.div<{ theme?: Theme }>`
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;

  .overlay-content {
    background-color: ${({ theme }: { theme?: Theme }) =>
      theme?.palette.background.paper};
    border-radius: 8px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    padding: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 100px;
  }
`;

interface DragAndDropProviderProps {
  children: React.ReactNode;
  onAssignUnit: (unitId: string, cfsId: string) => Promise<void>;
}

export const DragAndDropProvider = ({
  children,
  onAssignUnit,
}: DragAndDropProviderProps) => {
  const [activeUnit, setActiveUnit] = useState<IUnitShift | null>(null);
  const isDraggingRef = useRef(false);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 8 },
    }),
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    if (active.data.current) {
      const unitData = active.data.current as IUnitShift;
      setActiveUnit(unitData);
      isDraggingRef.current = true;
    }
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      try {
        if (over && active && isDraggingRef.current) {
          const unitId = String(active.id);
          const cfsId = String(over.id);

          // Extract unit shift ID from the drag data
          const unitShiftId = active.data.current?.unitShiftId;

          if (unitShiftId) {
            // If we have a unit shift ID, use it directly
            await onAssignUnit(unitShiftId, cfsId);
          } else {
            // Otherwise, fall back to using the unit ID
            await onAssignUnit(unitId, cfsId);
          }
        }
      } catch (error) {
        logger.error('❌ Drop error:', error);
      } finally {
        setActiveUnit(null);
        isDraggingRef.current = false;
      }
    },
    [onAssignUnit],
  );

  const getIcon = useMemo(() => {
    if (activeUnit?.unit.type?.agencyType === AgencyType.POLICE)
      return <IcPolice color={colors.grey[80]} style={{ marginTop: -1 }} />;
    if (activeUnit?.unit.type?.agencyType === AgencyType.EMS)
      return <IcMedicalCross />;
    return <IcFire color={colors.grey[80]} style={{ marginTop: -3 }} />;
  }, [activeUnit]);

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      autoScroll={true}
      collisionDetection={rectIntersection}
    >
      {children}
      <DragOverlay
        dropAnimation={{ ...defaultDropAnimation }}
        modifiers={[snapCenterToCursor]}
      >
        {activeUnit ? (
          <UnitDragOverlayContainer>
            <div className="overlay-content">
              <div style={{ fontWeight: 'bold' }}>{activeUnit.unit.name}</div>
              <Box
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: colors.grey[10],
                  padding: 8,
                  borderRadius: 6,
                }}
              >
                {getIcon}
                <Typography
                  variant="subtitle2"
                  style={{ marginLeft: 5 }}
                  color={colors.grey[80]}
                >
                  {activeUnit.unit.type?.name}
                </Typography>
              </Box>
            </div>
          </UnitDragOverlayContainer>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
